<template>
  <div class="c-graph" ref="canvas" v-click-outside="clickOutside">
    <LoadingChara v-if="isLoading">
      <template slot="title">
        データ取得中
      </template>
    </LoadingChara>
    <Loading v-else-if="isDrawing" />
    <Error v-else-if="isError">
      <template slot="title">
        データがありません。
      </template>
      <ul>
        <li>
          初回連携時はデータの取得に<span class="c-text--accent"
            >最大48時間程度</span
          >かかるため、<br />時間を置いてご確認ください。
        </li>
      </ul>
    </Error>
    <svg class="c-graph__svg" ref="svg" @click="clickOutside">
      <defs ref="defs">
        <GraphMask :size="size" />
      </defs>
      <g class="c-graph-label">
        <text x="0" y="12" text-anchor="start">平均順位</text>
        <text :x="size.canvasW" y="12" text-anchor="end">
          バブルの大きさ：
          <template v-if="isInflux">
            クリック率
          </template>
          <template v-else-if="isConversion">
            CV数
          </template>
        </text>
        <text :x="size.canvasW" :y="size.canvasH - 6" text-anchor="end">
          表示回数
        </text>
      </g>
      <g class="c-graph-bg" clip-path="url(#clipPath)">
        <GraphBg
          :data="x.bg"
          direction="x"
          :scale="d3.x"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gBgX"
        />
        <GraphBg
          :data="y.bg"
          direction="y"
          :scale="d3.y"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gBgY"
        />
      </g>
      <g class="c-graph-baseline" ref="gBaseLine">
        <rect
          class="c-graph-baseline__rect"
          x="0"
          y="0"
          :height="size.canvasH"
          :width="size.canvasW"
        ></rect>
        <g clip-path="url(#clipPath)">
          <line
            class="c-graph-baseline__line"
            y1="0"
            :y2="size.canvasH"
            ref="baseLineX"
          ></line>
          <line
            class="c-graph-baseline__line"
            x1="0"
            :x2="size.canvasW"
            ref="baseLineY"
          ></line>
          <line
            class="c-graph-baseline__line c-graph-baseline__line--move"
            y1="0"
            :y2="size.canvasH"
            ref="tempBaseLineX"
          ></line>
          <line
            class="c-graph-baseline__line c-graph-baseline__line--move"
            x1="0"
            :x2="size.canvasW"
            ref="tempBaseLineY"
          ></line>
        </g>
        <g ref="tempBaseLineMarkerX">
          <path
            class="c-graph-baseline__marker"
            :d="tempBaseLineMarker.top"
          ></path>
          <path
            class="c-graph-baseline__marker"
            :d="tempBaseLineMarker.bottom"
          ></path>
        </g>
        <g ref="tempBaseLineMarkerY">
          <path
            class="c-graph-baseline__marker"
            :d="tempBaseLineMarker.left"
          ></path>
          <path
            class="c-graph-baseline__marker"
            :d="tempBaseLineMarker.right"
          ></path>
        </g>
      </g>
      <g class="c-graph-area" clip-path="url(#clipPath)">
        <rect class="c-graph-area__zoom" ref="zoom"></rect>
      </g>
      <GraphFrame :size="size" />
      <g class="c-graph-axis">
        <GraphAxis
          :data="x.axis"
          position="bottom"
          :scale="d3.x"
          :scaleType="x.scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisX"
        />
        <GraphAxis
          :data="y.axis"
          position="left"
          :scale="d3.y"
          :scaleType="y.scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisY"
        />
      </g>
    </svg>
    <canvas
      class="c-graph-bubble"
      :width="size.graphW"
      :height="size.graphH"
      :style="{
        left: size.graph.left + 'px',
        top: size.graph.top + 'px'
      }"
      ref="gBubble"
      @mousemove="mousemove"
      @mouseleave="mouseleave"
      @mousedown="mousedown"
      @mouseup="mouseup"
    />
    <Button
      v-if="!isDisabled"
      variant="ruler"
      radius
      @click="toggleTempBaseLine"
    >
      基準線を編集
    </Button>
    <GraphTooltip
      :margin="tooltip.z"
      :position="{ x: tooltip.x, y: tooltip.y }"
      :size="size"
      :variant="tooltip.variant"
      :isVisible="tooltip.isVisible && !isDisabled ? true : false"
      @mouseenter="mouseenterTooltip"
      @mouseleave="mouseleaveTooltip"
    >
      <div class="c-graph-tooltip-summary">
        <ul class="c-graph-tooltip-list">
          <li class="c-graph-tooltip-list__item">
            <span class="c-graph-tooltip-list__key">
              流入URLが同じキーワード
            </span>
            <span class="c-graph-tooltip-list__value">
              {{ tooltip.countKeyword | separateNumber | nullToZero }}個
            </span>
          </li>
          <li class="c-graph-tooltip-list__item">
            <span class="c-graph-tooltip-list__key">
              検索ボリュームの合計
            </span>
            <span class="c-graph-tooltip-list__value">
              {{ tooltip.totalSearchVolume | separateNumber | nullToZero }}
            </span>
          </li>
        </ul>
        <div class="c-graph-tooltip-summary__btn">
          <p class="c-link--underline" @click="highlightBubble(tooltip.data)">
            ▶全てを表示
          </p>
        </div>
      </div>
      <ul class="c-graph-tooltip-list">
        <li class="c-graph-tooltip-list__item">
          <span class="c-graph-tooltip-list__key">キーワード</span>
          <span class="c-graph-tooltip-list__value c-text--ellipsis">
            {{ tooltip.data.keyword }}
          </span>
        </li>
        <li class="c-graph-tooltip-list__item">
          <span class="c-graph-tooltip-list__key">URL</span>
          <span class="c-graph-tooltip-list__value c-text--ellipsis">
            {{ tooltip.data.url | removeDomain(project.siteUrl) }}
          </span>
        </li>
        <li class="c-graph-tooltip-list__item">
          <span class="c-graph-tooltip-list__key">順位</span>
          <span class="c-graph-tooltip-list__value">
            {{ tooltip.data.rank | roundNumber }}
          </span>
        </li>
        <li v-if="isInflux" class="c-graph-tooltip-list__item">
          <span class="c-graph-tooltip-list__key">月間検索数</span>
          <span class="c-graph-tooltip-list__value">
            {{ tooltip.data.searchVolume | separateNumber | truncateNull }}
          </span>
        </li>
        <li class="c-graph-tooltip-list__item">
          <span class="c-graph-tooltip-list__key">表示回数</span>
          <span class="c-graph-tooltip-list__value">
            {{ tooltip.data.impressions | separateNumber | nullToZero }}
          </span>
        </li>
        <li class="c-graph-tooltip-list__item">
          <span class="c-graph-tooltip-list__key">クリック率</span>
          <span class="c-graph-tooltip-list__value">
            {{ tooltip.data.ctr | roundFloatingPointNumber(2) }}%
          </span>
        </li>
        <li class="c-graph-tooltip-list__item">
          <span class="c-graph-tooltip-list__key">流入数</span>
          <span class="c-graph-tooltip-list__value">
            {{ tooltip.data.influx | separateNumber | nullToZero }}
          </span>
        </li>
        <template v-if="isConversion">
          <li v-if="isConversionDirect" class="c-graph-tooltip-list__item">
            <span class="c-graph-tooltip-list__key">直接CV</span>
            <span class="c-graph-tooltip-list__value">
              {{ tooltip.data.directConversion | separateNumber | nullToZero }}
            </span>
          </li>
          <li v-if="isConversionAssisted" class="c-graph-tooltip-list__item">
            <span class="c-graph-tooltip-list__key">間接CV</span>
            <span class="c-graph-tooltip-list__value">
              {{
                tooltip.data.assistedConversion | separateNumber | nullToZero
              }}
            </span>
          </li>
          <li v-if="isConversionDirect" class="c-graph-tooltip-list__item">
            <span class="c-graph-tooltip-list__key">直接CVR</span>
            <span class="c-graph-tooltip-list__value">
              {{
                tooltip.data.directConversionRate | roundFloatingPointNumber(2)
              }}%
            </span>
          </li>
          <li v-if="isConversionAssisted" class="c-graph-tooltip-list__item">
            <span class="c-graph-tooltip-list__key">間接CVR</span>
            <span class="c-graph-tooltip-list__value">
              {{
                tooltip.data.assistedConversionRate
                  | roundFloatingPointNumber(2)
              }}%
            </span>
          </li>
        </template>
      </ul>
    </GraphTooltip>
  </div>
</template>

<script>
import Button from '@/components/Button';
import Error from '@/components/Error';
import GraphAxis from '@/components/GraphAxis';
import GraphBg from '@/components/GraphBg';
import GraphFrame from '@/components/GraphFrame';
import GraphMask from '@/components/GraphMask';
import GraphTooltip from '@/components/GraphTooltip';
import Loading from '@/components/Loading';
import LoadingChara from '@/components/LoadingChara';
import utils from '@/utils';
import * as d3 from 'd3';

export default {
  props: {
    conversionType: {
      type: String,
      required: true
    },
    dataType: {
      type: String,
      required: true
    },
    filter: {
      type: Object,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    hasData: {
      type: Boolean,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    }
  },
  components: {
    Button,
    Error,
    GraphAxis,
    GraphBg,
    GraphFrame,
    GraphMask,
    GraphTooltip,
    Loading,
    LoadingChara
  },
  data() {
    return {
      d3: {
        x: () => {},
        y: () => {},
        z: () => {}
      },
      el: {
        canvas: '',
        svg: '',
        gDefs: '',
        gBaseLine: '',
        gBubble: '',
        baseLineX: '',
        baseLineY: '',
        tempBaseLineX: '',
        tempBaseLineY: '',
        tempBaseLineMarkerX: '',
        tempBaseLineMarkerY: '',
        zoom: ''
      },
      baseLine: {
        key: {
          x: 'x',
          y: 'y'
        },
        x: 0,
        y: 10,
        cv_x: 0,
        cv_y: 10
      },
      bubble: {
        styles: [
          {
            fillStyle: 'rgba(254, 187, 206, .8)',
            shadowColor: 'rgba(254, 190, 208, .6)'
          },
          {
            fillStyle: 'rgba(80, 192, 142, .5)',
            shadowColor: 'rgba(157, 255, 211, .6)'
          },
          {
            fillStyle: 'rgba(255, 217, 112, .5)',
            shadowColor: 'rgba(255, 236, 183, .6)'
          }
        ],
        ctx: null,
        data: [],
        highlight: null,
        hover: null
      },
      tooltip: {
        data: {},
        variant: '',
        x: 0,
        y: 0,
        z: 0,
        isVisible: false
      },
      x: {
        axis: 5,
        bg: 5,
        domain: [],
        key: 'impressions',
        scaleType: 'number'
      },
      y: {
        axis: 5,
        bg: 5,
        domain: [],
        key: 'rank',
        scaleType: 'number'
      },
      z: {
        domain: [],
        key: '',
        scaleType: 'number'
      },
      zoom: {
        start: null,
        end: null
      },
      size: {
        axisXH: 30,
        axisYW: {
          left: 20,
          right: 0
        },
        bubbleR: [3, 40],
        canvasH: 0,
        canvasW: 0,
        frameW: 1,
        graphH: 0,
        graphW: 0,
        graph: {
          bottom: 0,
          center: 0,
          left: 0,
          right: 0,
          top: 0
        },
        offset: {
          bottom: 15,
          left: 0,
          right: 0,
          top: 23
        },
        tempBaseLineW: 1
      },
      transition: {
        duration: 0,
        ease: d3.easeCircle
      },
      status: this.$store.state.page.siteKeywordAnalysis,
      isDragging: false,
      isDrawed: false,
      isDrawing: false,
      isEditingBaseLine: false,
      isMousedowned: false
    };
  },
  computed: {
    tempBaseLineMarker() {
      return {
        top: `M-4.5,${this.size.graph.top - 9} L0,${this.size.graph.top -
          4} L4.5,${this.size.graph.top - 9} Z`,
        bottom: `M-4.5,${this.size.graph.bottom + 9} L0,${this.size.graph
          .bottom + 4} L4.5,${this.size.graph.bottom + 9} Z`,
        left: `M${this.size.graph.left - 9},-4.5 L${this.size.graph.left -
          4},0 L${this.size.graph.left - 9},4.5 Z`,
        right: `M${this.size.graph.right + 9},-4.5 L${this.size.graph.right +
          4},0 L${this.size.graph.right + 9},4.5 Z`
      };
    },
    isConversion() {
      return this.dataType === 'conversion';
    },
    isConversionAssisted() {
      return this.isConversion && this.conversionType === 'assisted';
    },
    isConversionDirect() {
      return this.isConversion && this.conversionType === 'direct';
    },
    isDisabled() {
      return this.isLoading || this.isDrawing || this.isError;
    },
    isError() {
      return !this.hasData;
    },
    isInflux() {
      return this.dataType === 'influx';
    }
  },
  watch: {
    items: {
      handler() {
        // 初期化
        this.bubble.hover = null;
        this.bubble.highlight = null;
        this.update();
      },
      deep: true
    }
  },
  mounted() {
    this.setEl();
    this.setSize();
    this.update();
    window.addEventListener('resize', this.resize);
  },
  beforeDestroy() {
    document.body.removeEventListener('mousemove', this.mousemoveTempBaseLine);
    document.body.removeEventListener('mousemove', this.mousemoveZoom);
    document.body.removeEventListener('mouseup', this.mouseupZoom);
    window.removeEventListener('resize', this.resize);
  },
  methods: {
    update() {
      if (this.isLoading) {
        return;
      }
      this.isDrawing = true;
      this.hideTooltip();
      this.setData();
      this.setScale();
      this.draw();
      this.isDrawed = true;
      this.isDrawing = false;
    },
    setEl() {
      this.el.canvas = this.$refs.canvas;
      this.el.svg = d3.select(this.$refs.svg);
      this.el.gDefs = d3.select(this.$refs.defs);
      this.el.gBaseLine = d3.select(this.$refs.gBaseLine);
      this.el.gBubble = d3.select(this.$refs.gBubble);
      this.el.baseLineX = d3.select(this.$refs.baseLineX);
      this.el.baseLineY = d3.select(this.$refs.baseLineY);
      this.el.tempBaseLineX = d3.select(this.$refs.tempBaseLineX);
      this.el.tempBaseLineY = d3.select(this.$refs.tempBaseLineY);
      this.el.tempBaseLineMarkerX = d3.select(this.$refs.tempBaseLineMarkerX);
      this.el.tempBaseLineMarkerY = d3.select(this.$refs.tempBaseLineMarkerY);
      this.el.zoom = d3.select(this.$refs.zoom);
      this.bubble.ctx = this.el.gBubble.node().getContext('2d');
    },
    setSize() {
      this.size.canvasH = this.el.canvas.clientHeight;
      this.size.canvasW = this.el.canvas.clientWidth;

      this.size.graphH =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW * 2 -
        this.size.offset.top -
        this.size.offset.bottom;

      this.size.graphW =
        this.size.canvasW -
        this.size.axisYW.left -
        this.size.axisYW.right -
        this.size.frameW * 2 -
        this.size.offset.left -
        this.size.offset.right;

      this.size.graph.bottom =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW -
        this.size.offset.bottom;

      this.size.graph.center = Math.ceil(
        (this.size.canvasW + this.size.axisYW.left) / 2
      );

      this.size.graph.left =
        this.size.offset.left + this.size.axisYW.left + this.size.frameW;

      this.size.graph.right =
        this.size.canvasW -
        this.size.axisYW.right -
        this.size.frameW -
        this.size.offset.right;

      this.size.graph.top = this.size.offset.top + this.size.frameW;
    },
    setData() {
      // xyzのkeyなどをがさごそする
      if (this.isInflux) {
        this.baseLine.key.x = 'x';
        this.baseLine.key.y = 'y';
        this.z.key = 'ctr';
      } else if (this.isConversion) {
        this.baseLine.key.x = 'cv_x';
        this.baseLine.key.y = 'cv_y';
        // 直接コンバージョン基準
        if (this.isConversionDirect) {
          this.z.key = 'directConversion';
          // 間接コンバージョン基準
        } else if (this.isConversionAssisted) {
          this.z.key = 'assistedConversion';
        }
      }

      // バブルの大きい順に並び替える
      // （大きいバブルと小さいバブルが重なる時に、小さいバブルにhoverできるようにする）
      this.bubble.data = utils.sortNumber(
        this.items.slice(),
        'desc',
        this.z.key
      );

      this.x.domain = this.filter[this.x.key];
      this.y.domain = this.filter[this.y.key];
      this.z.domain = this.filter[this.z.key];

      const saveData = this.$store.state.page.siteKeywordAnalysis[
        this.project.id
      ];
      if (saveData && saveData.baseLine) {
        this.baseLine[this.baseLine.key.x] =
          saveData.baseLine[this.baseLine.key.x];
        this.baseLine[this.baseLine.key.y] =
          saveData.baseLine[this.baseLine.key.y];
      } else {
        this.baseLine[this.baseLine.key.x] =
          Math.abs(this.filter[this.x.key][0] - this.filter[this.x.key][1]) / 2;
      }
    },
    setScale() {
      this.d3.x = d3
        .scaleLinear()
        .domain(this.x.domain)
        .rangeRound([this.size.graph.left, this.size.graph.right]);

      this.d3.y = d3
        .scaleLinear()
        .domain(this.y.domain)
        .rangeRound([this.size.graph.top, this.size.graph.bottom]);

      this.d3.z = d3
        .scaleLinear()
        .domain(this.z.domain)
        .rangeRound(this.size.bubbleR)
        .clamp(true);
    },
    draw() {
      this.$nextTick(() => {
        this.drawBubble();
      });
      this.drawBaseLine();
      this.$refs.gAxisX.draw();
      this.$refs.gAxisY.draw();
      this.$refs.gBgX.draw();
      this.$refs.gBgY.draw();
    },
    drawBubble() {
      const ctx = this.bubble.ctx;
      ctx.clearRect(0, 0, this.size.graphW, this.size.graphH);
      ctx.save();
      ctx.translate(-this.size.graph.left, -this.size.graph.top);

      this.bubble.data.forEach(d => {
        const x = this.d3.x(d[this.x.key]);
        const y = this.d3.y(d[this.y.key]);
        const radius = this.d3.z(d[this.z.key]);
        const style = this.bubble.styles[d.variant];
        const isHover = this.isHoverBubble(d);
        const isHighlighting = this.isHighlightBubble(d);
        const isLowlighting = this.isLowlightBubble(d);

        ctx.fillStyle = style.fillStyle;
        ctx.globalAlpha = isLowlighting ? 0.2 : 1;

        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI, true);
        ctx.closePath();
        ctx.fill();

        if (!isLowlighting) {
          ctx.save();
          if (isHover || isHighlighting) {
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 2;
            ctx.shadowColor = style.shadowColor;
            ctx.shadowBlur = 10;
            ctx.stroke();
          }
          if (isHighlighting) {
            const fontSize = 10;
            ctx.fillStyle = '#34404e';
            ctx.font = `${fontSize}px 'Hiragino Kaku Gothic ProN', 'Noto Sans JP', Meiryo, メイリオ`;
            ctx.textBaseline = 'middle';

            const textW = ctx.measureText(d.keyword).width;
            const textH = fontSize;
            let textAlign = 'start';
            let textX = x + radius + 4;
            let textY = y;

            // テキストが見切れないように調整する
            if (textX + textW > this.size.graph.right) {
              textAlign = 'end';
              textX = x - radius - 4;
            }
            if (textY - textH / 2 < this.size.graph.top) {
              textY = this.size.graph.top + textH / 2;
            } else if (textY + textH / 2 > this.size.graph.bottom) {
              textY = this.size.graph.bottom - textH / 2;
            }

            ctx.textAlign = textAlign;
            ctx.fillText(d.keyword, textX, textY);
          }
          ctx.restore();
        }
      });

      ctx.restore();
    },
    attrBaseLineX(selection) {
      let x;
      if (
        this.x.domain[0] !== this.x.domain[1] ||
        this.baseLine[this.baseLine.key.x] === this.x.domain[0]
      ) {
        x = this.d3.x(this.baseLine[this.baseLine.key.x]);
      }
      selection.attr('x1', x).attr('x2', x);
    },
    attrBaseLineY(selection) {
      let y;
      if (
        this.y.domain[0] !== this.y.domain[1] ||
        this.baseLine[this.baseLine.key.y] === this.y.domain[0]
      ) {
        y = this.d3.y(this.baseLine[this.baseLine.key.y]);
      }
      selection.attr('y1', y).attr('y2', y);
    },
    drawBaseLine() {
      this.el.baseLineX.call(this.attrBaseLineX);
      this.el.baseLineY.call(this.attrBaseLineY);
    },
    attrBaseLine(x, y) {
      this.baseLine[this.baseLine.key.x] = this.d3.x.invert(x);
      this.baseLine[this.baseLine.key.y] = this.d3.y.invert(y);

      this.el.baseLineX
        .attr('x1', this.d3.x(this.baseLine[this.baseLine.key.x]))
        .attr('x2', this.d3.x(this.baseLine[this.baseLine.key.x]));

      this.el.baseLineY
        .attr('y1', this.d3.y(this.baseLine[this.baseLine.key.y]))
        .attr('y2', this.d3.y(this.baseLine[this.baseLine.key.y]));

      // 基準線の位置をlocalStorageに保存する
      const baseLineObj = {
        x: this.baseLine.x,
        y: this.baseLine.y,
        cv_x: this.baseLine.cv_x,
        cv_y: this.baseLine.cv_y
      };
      if (!this.status[this.project.id]) {
        this.status[this.project.id] = {
          baseLine: baseLineObj
        };
      } else {
        this.status[this.project.id].baseLine = baseLineObj;
      }
      this.setStatus(this.status);
    },
    attrTempBaseLine(x, y) {
      this.el.tempBaseLineX.attr('x1', x).attr('x2', x);
      this.el.tempBaseLineMarkerX.attr('transform', `translate(${x}, 0)`);
      this.el.tempBaseLineY.attr('y1', y).attr('y2', y);
      this.el.tempBaseLineMarkerY.attr('transform', `translate(0, ${y})`);
    },
    hoverBubble(bubble) {
      this.bubble.hover = bubble;
      this.el.gBubble.classed('is-hover', true);
      this.drawBubble();
      this.showTooltip(bubble);
    },
    unhoverBubble() {
      this.bubble.hover = null;
      this.el.gBubble.classed('is-hover', false);
      this.drawBubble();
      this.hideTooltip();
    },
    highlightBubble(d) {
      this.bubble.highlight = d;
      this.drawBubble();
    },
    unhighlightBubble() {
      this.bubble.highlight = null;
      this.drawBubble();
    },
    resetBubble() {
      // hoverかhighlight状態のbubbleがある時のみ再描画する
      if (this.bubble.hover || this.bubble.highlight) {
        this.unhoverBubble();
        this.unhighlightBubble();
      }
    },
    isHoverBubble(d) {
      if (this.bubble.hover && d.id === this.bubble.hover.id) {
        return true;
      }
    },
    isHighlightBubble(d) {
      if (this.bubble.highlight && d.url === this.bubble.highlight.url) {
        return true;
      }
    },
    isLowlightBubble(d) {
      if (this.bubble.highlight && d.url !== this.bubble.highlight.url) {
        return true;
      }
    },
    getPos(e) {
      const rect = this.el.gBubble.node().getBoundingClientRect();
      return {
        x: e.clientX - rect.left + this.size.graph.left,
        y: e.clientY - rect.top + this.size.graph.top
      };
    },
    getTargetBubble(x, y) {
      const bubble = this.bubble.data
        .slice()
        .reverse()
        .find(d => {
          const bubbleX = this.d3.x(d[this.x.key]);
          const bubbleY = this.d3.y(d[this.y.key]);
          const radius = this.d3.z(d[this.z.key]);
          if (
            !this.isLowlightBubble(d) &&
            x >= bubbleX - radius &&
            x <= bubbleX + radius &&
            y >= bubbleY - radius &&
            y <= bubbleY + radius
          ) {
            return true;
          }
        });
      return bubble;
    },
    getZoom() {
      const x = utils.sortNumber([
        this.getZoomX(this.zoom.start.x),
        this.getZoomX(this.zoom.end.x)
      ]);
      const y = utils.sortNumber([
        this.getZoomY(this.zoom.start.y),
        this.getZoomY(this.zoom.end.y)
      ]);
      return { x, y };
    },
    getZoomX(x) {
      let value = Math.round(this.d3.x.invert(x));
      if (value < this.x.domain[0]) {
        value = this.x.domain[0];
      } else if (value > this.x.domain[1]) {
        value = this.x.domain[1];
      }
      return value;
    },
    getZoomY(y) {
      let value = Math.round(this.d3.y.invert(y));
      if (value < this.y.domain[0]) {
        value = this.y.domain[0];
      } else if (value > this.y.domain[1]) {
        value = this.y.domain[1];
      }
      return value;
    },
    clickOutside() {
      // グラフの範囲外をクリックしたら基準線の編集をやめる
      this.hideTempBaseLine();
      this.resetBubble();
    },
    mouseenterTooltip() {
      this.hoverBubble(this.tooltip.data);
    },
    mouseleave() {
      this.unhoverBubble();
    },
    mouseleaveTooltip(e) {
      // バブルcanvasの外側にmouseleaveした時のみhoverを解除する
      // mouseleaveした地点がバブル上だった場合に、tooltipがちらつく（一瞬消えてまたすぐ表示される）のを回避するため
      const rect = this.el.gBubble.node().getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        this.unhoverBubble();
      }
    },
    mousedown(e) {
      if (this.isEditingBaseLine) {
        return;
      }
      this.isMousedowned = true;
      this.zoom.start = this.getPos(e);
      this.unhoverBubble();
      document.body.addEventListener('mousemove', this.mousemoveZoom);
      document.body.addEventListener('mouseup', this.mouseupZoom);
    },
    mousemove(e) {
      e.preventDefault();
      if (this.isMousedowned || this.isEditingBaseLine) {
        return;
      }
      const pos = this.getPos(e);
      const bubble = this.getTargetBubble(pos.x, pos.y);
      if (bubble) {
        // hover対象が変わった時のみ再描画する
        if (!this.bubble.hover || bubble.id !== this.bubble.hover.id) {
          this.hoverBubble(bubble);
        }
      } else if (this.bubble.hover) {
        this.unhoverBubble();
      }
    },
    mousemoveTempBaseLine(e) {
      e.preventDefault();
      const pos = this.getPos(e);
      const tempBaseLineHalfW = this.size.tempBaseLineW / 2;
      const minX = this.size.graph.left + tempBaseLineHalfW;
      const maxX = this.size.graph.right - tempBaseLineHalfW;
      const minY = this.size.graph.top + tempBaseLineHalfW;
      const maxY = this.size.graph.bottom - tempBaseLineHalfW;

      if (pos.x < minX) {
        pos.x = minX;
      } else if (pos.x > maxX) {
        pos.x = maxX;
      }
      if (pos.y < minY) {
        pos.y = minY;
      } else if (pos.y > maxY) {
        pos.y = maxY;
      }
      this.attrTempBaseLine(pos.x, pos.y);
    },
    mousemoveZoom(e) {
      e.preventDefault();
      this.isDragging = true;
      this.zoom.end = this.getPos(e);
      const x = utils.sortNumber([this.zoom.start.x, this.zoom.end.x]);
      const y = utils.sortNumber([this.zoom.start.y, this.zoom.end.y]);

      this.el.zoom
        .attr('x', x[0])
        .attr('y', y[0])
        .attr('height', y[1] - y[0])
        .attr('width', x[1] - x[0])
        .classed('is-hidden', false);
    },
    mouseup(e) {
      if (this.isDragging) {
        return;
      }
      if (this.isEditingBaseLine) {
        // 基準線の編集を完了する
        const pos = this.getPos(e);
        this.attrBaseLine(pos.x, pos.y);
        this.hideTempBaseLine();
      } else {
        // バブルデビューン
        const pos = this.getPos(e);
        const targetBubble = this.getTargetBubble(pos.x, pos.y);
        if (targetBubble) {
          this.$emit('scrollTo', targetBubble);
        }
      }
    },
    mouseupZoom() {
      // xとy両方移動している時のみズームする
      if (
        this.isDragging &&
        this.zoom.start.x !== this.zoom.end.x &&
        this.zoom.start.y !== this.zoom.end.y
      ) {
        const zoom = this.getZoom();
        this.$emit('zoom', zoom);
      }
      this.isDragging = false;
      this.isMousedowned = false;
      this.zoom.start = null;
      this.zoom.end = null;
      this.el.zoom.classed('is-hidden', true);
      document.body.removeEventListener('mousemove', this.mousemoveZoom);
      document.body.removeEventListener('mouseup', this.mouseupZoom);
    },
    toggleTempBaseLine() {
      this.isEditingBaseLine = !this.isEditingBaseLine;
      this.unhoverBubble();

      if (this.isEditingBaseLine) {
        const x = this.d3.x(this.baseLine[this.baseLine.key.x]);
        const y = this.d3.y(this.baseLine[this.baseLine.key.y]);
        this.attrTempBaseLine(x, y);
        this.el.gBaseLine.classed('is-active', true);
        document.body.addEventListener('mousemove', this.mousemoveTempBaseLine);
        // タスクリストを閉じる
        this.$store.dispatch('task/setStatus', { isActive: false });
      } else {
        this.hideTempBaseLine();
      }
    },
    hideTempBaseLine() {
      this.isEditingBaseLine = false;
      this.el.gBaseLine.classed('is-active', false);
      document.body.removeEventListener(
        'mousemove',
        this.mousemoveTempBaseLine
      );
    },
    showTooltip(d) {
      let countKeyword = 0;
      let totalSearchVolume = 0;
      for (let i = 0; i < this.items.length; i++) {
        if (d.url === this.items[i].url) {
          countKeyword += 1;
          totalSearchVolume += this.items[i].searchVolume;
        }
      }
      this.tooltip.data = d;
      this.tooltip.countKeyword = countKeyword;
      this.tooltip.totalSearchVolume = totalSearchVolume;
      this.tooltip.variant = 'b' + d.variant;
      this.tooltip.x = this.d3.x(d[this.x.key]);
      this.tooltip.y = this.d3.y(d[this.y.key]);
      this.tooltip.z = this.d3.z(d[this.z.key]);
      this.tooltip.isVisible = true;
    },
    hideTooltip() {
      this.tooltip.isVisible = false;
    },
    resize() {
      const oldCanvasW = this.size.canvasW;
      this.hideTempBaseLine();
      this.setSize();

      if (!this.isDrawed) {
        return;
      }
      if (this.size.canvasW !== oldCanvasW) {
        this.setScale();
        this.el.baseLineX.call(this.attrBaseLineX);
        this.$refs.gAxisX.resize();
        this.$refs.gBgX.resize();
        this.$refs.gBgY.resize();

        this.$nextTick(() => {
          this.drawBubble();
        });
      }
    },
    setStatus(status) {
      this.$store.dispatch('page/setStatus', {
        page: 'siteKeywordAnalysis',
        status: status
      });
    }
  }
};
</script>
